import { useState, type ComponentType, type ReactNode } from 'react';
import {
    ArrowUpCircleIcon,
    ArrowUturnLeftIcon,
    ChevronDownIcon,
    ChevronUpIcon
} from '@heroicons/react/24/outline';
import type {
    VirtualAppointmentHandoffDto,
    VirtualAppointmentHandoffKind
} from '../../../../services/virtualAppointments';
import { useIsTextClamped } from '../../../../hooks/useIsTextClamped';
import { formatShortDate } from '../../../../utils/dates';

interface HandoffPresentation {
    label: string;
    icon: ComponentType<{ className?: string }>;
    rowClassName: string;
    bubbleClassName: string;
    hoverClassName: string;
    labelClassName: string;
    iconClassName: string;
}

// Questions sit left in amber, answers right in blue, so the back and forth reads like a chat.
const PRESENTATION: Record<VirtualAppointmentHandoffKind, HandoffPresentation> = {
    escalation: {
        label: 'Question to Clinical',
        icon: ArrowUpCircleIcon,
        rowClassName: 'justify-start',
        bubbleClassName: 'bg-amber-50 border-amber-200 rounded-tl-sm',
        hoverClassName: 'hover:border-amber-300 hover:bg-amber-100/60',
        labelClassName: 'text-amber-900',
        iconClassName: 'text-amber-600'
    },
    'hand-back': {
        label: 'Answer to DM',
        icon: ArrowUturnLeftIcon,
        rowClassName: 'justify-end',
        bubbleClassName: 'bg-blue-50 border-blue-200 rounded-tr-sm',
        hoverClassName: 'hover:border-blue-300 hover:bg-blue-100/60',
        labelClassName: 'text-blue-900',
        iconClassName: 'text-blue-600'
    }
};

function handoffByline({ byName, at, movedCount }: VirtualAppointmentHandoffDto): string {
    const moved = movedCount > 0 && `Moved ${movedCount} ${movedCount === 1 ? 'VA' : 'VAs'}`;
    return [byName, formatShortDate(at), moved].filter(Boolean).join(' · ');
}

interface BubbleFrameProps {
    interactive: boolean;
    expanded: boolean;
    onToggle: () => void;
    className: string;
    hoverClassName: string;
    children: ReactNode;
}

// Only a clamped (or expanded) older message is clickable; the rest stay plain text.
function BubbleFrame({
    interactive,
    expanded,
    onToggle,
    className,
    hoverClassName,
    children
}: BubbleFrameProps) {
    if (!interactive) return <div className={className}>{children}</div>;
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            title={expanded ? 'Show less' : 'Show more'}
            className={`${className} ${hoverClassName} text-left transition-colors duration-200`}
        >
            {children}
        </button>
    );
}

interface VaHandoffMessageProps {
    handoff: VirtualAppointmentHandoffDto;
    /** The latest message always shows in full. */
    latest: boolean;
}

export function VaHandoffMessage({ handoff, latest }: VaHandoffMessageProps) {
    const [toggledOpen, setToggledOpen] = useState(false);
    const [textElement, setTextElement] = useState<HTMLSpanElement | null>(null);
    const expanded = latest || toggledOpen;
    const clamped = useIsTextClamped(expanded ? null : textElement, handoff.reason);
    const interactive = !latest && (clamped || toggledOpen);
    const look = PRESENTATION[handoff.kind];
    const Icon = look.icon;
    const Chevron = toggledOpen ? ChevronUpIcon : ChevronDownIcon;
    return (
        <li className={`flex ${look.rowClassName}`}>
            <BubbleFrame
                interactive={interactive}
                expanded={toggledOpen}
                onToggle={() => setToggledOpen((prev) => !prev)}
                hoverClassName={look.hoverClassName}
                className={`max-w-[85%] min-w-0 flex flex-col gap-0.5 border rounded-xl px-3 py-2 ${look.bubbleClassName}`}
            >
                <span className="flex items-center gap-1.5 min-w-0 text-[11px]">
                    <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${look.iconClassName}`} />
                    <span className={`font-semibold flex-shrink-0 ${look.labelClassName}`}>
                        {look.label}
                    </span>
                    <span className="text-gray-500 truncate">{handoffByline(handoff)}</span>
                    {interactive && (
                        <Chevron className="w-3.5 h-3.5 flex-shrink-0 ml-auto text-gray-400" />
                    )}
                </span>
                <span
                    ref={setTextElement}
                    className={`text-xs text-gray-800 leading-relaxed whitespace-pre-wrap break-words ${
                        expanded ? 'block' : 'line-clamp-2'
                    }`}
                >
                    {handoff.reason}
                </span>
            </BubbleFrame>
        </li>
    );
}
